/**
 * Print Failure / Trap chapter — Canva page-11.
 * Header + luopan, named trap, what goes wrong, notice-when x2, how to get out.
 * Weeks live on {@link PdfChapterWeeks}; this sheet is trap only.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import { AlertTriangle, ArrowRight, Flag } from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import wayThroughMountains from "../../../../assets/monthly-consultation/pdf/trap-way-through-mountains.png";
import { PdfChapterShell, PdfCornerLuopan } from "../atoms";
import { clipPdfText, stripPrintDashes } from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardWhite,
  charcoal,
  cream,
  displayFontFamily,
  green,
  mutedGray,
  paleGreen,
  palePink,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF failure / trap chapter.
 */
export interface PdfChapterFailureProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/** Soft cap for the "what goes wrong" paragraph. */
const DESC_CLIP = 240;
/** Soft cap for a notice-when card. */
const NOTICE_CLIP = 100;
/** Soft cap for the exit-move paragraph. */
const EXIT_CLIP = 180;

/** Empty-state trap name when the bundle field is blank. */
const NAME_FALLBACK = "This month's named trap";
/** Empty-state description when the bundle field is blank. */
const DESC_FALLBACK =
  "The common mistake this month is repeating an old pattern instead of taking one opposite action.";
/** Empty-state notice-when lines (product chrome, not client sample copy). */
const NOTICE_FALLBACKS = [
  "You repeat an old pattern instead of choosing a new move.",
  "You delay the one action that would change the week.",
] as const;
/** Empty-state exit move when the bundle field is blank. */
const EXIT_FALLBACK =
  "Name the pattern in one sentence. Then do the opposite once.";

/**
 * Thin gold rule with diamonds at both ends and the center (header divider).
 *
 * @param props - Stroke color
 * @returns Rule row
 */
const PdfDiamondRule: React.FC<{ readonly color: string }> = ({ color }) => {
  const stroke =
    typeof color === "string" && color.trim().length > 0
      ? color.trim()
      : tanGold;
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          marginRight: 1,
        }}
      />
      <span
        style={{
          flex: 1,
          height: 1,
          background: stroke,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          margin: "0 4px",
        }}
      />
      <span
        style={{
          flex: 1,
          height: 1,
          background: stroke,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          marginLeft: 1,
        }}
      />
    </div>
  );
};

/**
 * Canva "YOU WILL NOTICE IT WHEN" rule: label centered between diamond-capped lines.
 *
 * @param props - Uppercase label
 * @returns Labeled rule
 */
const PdfCenteredSectionRule: React.FC<{ readonly label: string }> = ({
  label,
}) => {
  const safe = typeof label === "string" ? label.trim() : "";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        margin: "4px 0",
        fontFamily: bodyFontFamily,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            background: tanGold,
            transform: "rotate(45deg)",
            flexShrink: 0,
          }}
        />
        <span
          style={{
            flex: 1,
            height: 1,
            background: tanGold,
            marginLeft: 1,
          }}
        />
      </span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: terracotta,
          flexShrink: 0,
        }}
      >
        {safe}
      </span>
      <span
        aria-hidden="true"
        style={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
        }}
      >
        <span
          style={{
            flex: 1,
            height: 1,
            background: tanGold,
            marginRight: 1,
          }}
        />
        <span
          style={{
            width: 6,
            height: 6,
            background: tanGold,
            transform: "rotate(45deg)",
            flexShrink: 0,
          }}
        />
      </span>
    </div>
  );
};

/**
 * Vertical terracotta accent used before "WHAT GOES WRONG".
 *
 * @returns Accent bar
 */
const PdfLabelAccentBar: React.FC = () => (
  <span
    aria-hidden="true"
    style={{
      width: 3,
      height: 12,
      borderRadius: 2,
      background: terracotta,
      flexShrink: 0,
    }}
  />
);

/**
 * Compact mountain-path mark for the How To Get Out card.
 *
 * @returns Decorative SVG
 */
const ExitPathMark: React.FC = () => (
  <svg
    width="96"
    height="72"
    viewBox="0 0 96 72"
    fill="none"
    aria-hidden="true"
    style={{
      position: "absolute",
      right: 10,
      bottom: 6,
      opacity: 0.3,
      pointerEvents: "none",
    }}
  >
    <path
      d="M4 64 L28 30 L42 46 L60 16 L92 64"
      stroke={green}
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M60 16 C68 28, 74 42, 78 56"
      stroke={green}
      strokeWidth="1.4"
      strokeDasharray="2 3"
      strokeLinecap="round"
    />
    <path
      d="M60 16 L60 6 M55 11 L60 6 L65 11"
      stroke={green}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="60" cy="16" r="2.4" fill={green} />
  </svg>
);

/**
 * One step in the bottom “way through” visual path.
 */
interface WayThroughStep {
  /** Uppercase step label. */
  readonly label: string;
  /** Short supporting line. */
  readonly body: string;
  /** Accent color for icon chrome. */
  readonly accent: string;
  /** Soft fill behind the step. */
  readonly tint: string;
  /** Lucide icon. */
  readonly Icon: LucideIcon;
}

/**
 * Resolve the webpack URL for the Trap “way through” mountain art.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function wayThroughMountainsSrc(): string {
  return typeof wayThroughMountains === "string"
    ? wayThroughMountains.trim()
    : "";
}

/**
 * Bottom page filler: one intentional visual board that absorbs leftover
 * A4 height so copy cards can stay content-sized (no empty stretched boxes).
 *
 * @param props - Named trap and exit snippets for the path labels
 * @returns Growing visual path panel
 */
const TrapWayThroughPanel: React.FC<{
  readonly trapName: string;
  readonly exitMove: string;
}> = ({ trapName, exitMove }) => {
  const mountains = wayThroughMountainsSrc();
  const steps: readonly WayThroughStep[] = [
    {
      label: "Trap",
      body: clipPdfText(trapName, 42),
      accent: terracotta,
      tint: palePink,
      Icon: AlertTriangle,
    },
    {
      label: "Notice",
      body: "Catch the old script early",
      accent: tanGold,
      tint: cream,
      Icon: Flag,
    },
    {
      label: "Exit",
      body: clipPdfText(exitMove, 48),
      accent: green,
      tint: paleGreen,
      Icon: ArrowRight,
    },
  ];

  return (
    <div
      style={{
        flex: "1 1 auto",
        minHeight: 160,
        background: "transparent",
        borderRadius: 0,
        padding: "10px 4px 4px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        fontFamily: bodyFontFamily,
      }}
    >
      {mountains.length > 0 ? (
        <img
          src={mountains}
          alt=""
          aria-hidden="true"
          className="mc-pdf-trap-way-mountains"
          style={{
            position: "absolute",
            right: -4,
            bottom: -2,
            width: 268,
            height: 168,
            objectFit: "contain",
            objectPosition: "right bottom",
            opacity: 0.92,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      ) : null}

      <p
        style={{
          margin: 0,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: tanGold,
          position: "relative",
          zIndex: 1,
          paddingLeft: 8,
        }}
      >
        The way through
      </p>
      <p
        style={{
          margin: "6px 0 0",
          fontSize: 12,
          lineHeight: 1.4,
          color: mutedGray,
          position: "relative",
          zIndex: 1,
          maxWidth: "70%",
          paddingLeft: 8,
        }}
      >
        Name it, catch it, then take one opposite move.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr auto 1fr",
          gap: 8,
          alignItems: "stretch",
          marginTop: 14,
          position: "relative",
          zIndex: 1,
          padding: "0 4px",
        }}
      >
        {steps.map((step, index) => {
          const Icon = step.Icon;
          return (
            <React.Fragment key={step.label}>
              <div
                style={{
                  background: step.tint,
                  border: `1px solid ${step.accent}22`,
                  borderRadius: 14,
                  padding: "12px 12px",
                  boxSizing: "border-box",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: cardWhite,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={16} color={step.accent} strokeWidth={1.8} />
                </span>
                <p
                  style={{
                    margin: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: step.accent,
                  }}
                >
                  {step.label}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontFamily: displayFontFamily,
                    fontSize: 12,
                    lineHeight: 1.4,
                    color: charcoal,
                  }}
                >
                  {step.body}
                </p>
              </div>
              {index < steps.length - 1 ? (
                <div
                  aria-hidden="true"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: tanGold,
                    fontSize: 18,
                    fontWeight: 700,
                    lineHeight: 1,
                    paddingBottom: 8,
                  }}
                >
                  →
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Collect two notice-when lines from live copy, padding with fallbacks.
 *
 * @param lines - Bundle `howItShows`
 * @returns Exactly two clipped sentences
 */
function noticeWhenLines(lines: readonly string[]): string[] {
  const live = Array.isArray(lines)
    ? lines
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    : [];
  const out: string[] = [];
  for (const line of live) {
    out.push(stripPrintDashes(clipPdfText(line, NOTICE_CLIP)));
    if (out.length >= 2) {
      return out;
    }
  }
  for (const fallback of NOTICE_FALLBACKS) {
    if (out.length >= 2) {
      break;
    }
    out.push(fallback);
  }
  return out.slice(0, 2);
}

/**
 * The trap to watch this month — one A4 sheet, no week tiles.
 */
export const PdfChapterFailure: React.FC<PdfChapterFailureProps> = ({
  bundle,
  pageNumber,
}) => {
  const mode = bundle.failureMode;
  const nameRaw = typeof mode.name === "string" ? mode.name.trim() : "";
  const name = stripPrintDashes(nameRaw.length > 0 ? nameRaw : NAME_FALLBACK);
  const descriptionRaw =
    typeof mode.description === "string" ? mode.description.trim() : "";
  const description = stripPrintDashes(
    clipPdfText(
      descriptionRaw.length > 0 ? descriptionRaw : DESC_FALLBACK,
      DESC_CLIP
    )
  );
  const howItShows = Array.isArray(mode.howItShows) ? mode.howItShows : [];
  const notices = noticeWhenLines(howItShows);
  const exitRaw = typeof mode.exitMove === "string" ? mode.exitMove.trim() : "";
  const exitMove = stripPrintDashes(
    clipPdfText(exitRaw.length > 0 ? exitRaw : EXIT_FALLBACK, EXIT_CLIP)
  );

  return (
    <PdfChapterShell
      chapterId="failure"
      eyebrow=""
      title=""
      hideHeader={true}
      breakBefore="always"
      fillPage={true}
      compact
      pageNumber={pageNumber}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          flex: "1 1 auto",
          minHeight: 0,
          height: "100%",
          fontFamily: bodyFontFamily,
        }}
      >
        <div
          className="mc-pdf-corner-luopan-band"
          style={{ flexShrink: 0 }}
        >
          <PdfCornerLuopan />
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              position: "relative",
              zIndex: 1,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: palePink,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginTop: 4,
              }}
            >
              <AlertTriangle size={20} color={terracotta} strokeWidth={1.8} />
            </span>
            <div style={{ minWidth: 0, flex: 1, paddingRight: 24 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: terracotta,
                }}
              >
                + TRAP
              </p>
              <h2
                style={{
                  margin: "4px 0 0",
                  fontFamily: displayFontFamily,
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                  color: charcoal,
                }}
              >
                The Trap To Watch This Month
              </h2>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: mutedGray,
                }}
              >
                This is the most common way this month goes wrong.
              </p>
            </div>
          </div>
          <div style={{ marginTop: 12, position: "relative", zIndex: 1 }}>
            <PdfDiamondRule color={tanGold} />
          </div>
        </div>

        <div
          className="mc-pdf-card"
          style={{
            display: "flex",
            gap: 14,
            alignItems: "center",
            background: palePink,
            border: `1px solid ${terracotta}22`,
            borderRadius: 16,
            padding: "14px 16px",
            fontFamily: bodyFontFamily,
            flexShrink: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: `${terracotta}18`,
              color: terracotta,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={18} color={terracotta} strokeWidth={1.8} />
          </span>
          <div style={{ minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: terracotta,
              }}
            >
              Named trap
            </p>
            <p
              style={{
                margin: "6px 0 0",
                fontFamily: displayFontFamily,
                fontSize: 18,
                fontWeight: 700,
                color: charcoal,
                lineHeight: 1.25,
              }}
            >
              {name}
            </p>
          </div>
        </div>

        <div
          className="mc-pdf-card"
          style={{
            background: cream,
            border: `1px solid ${border}`,
            borderRadius: 16,
            padding: "14px 16px",
            fontFamily: bodyFontFamily,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <PdfLabelAccentBar />
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: terracotta,
              }}
            >
              What goes wrong
            </p>
          </div>
          <p
            style={{
              margin: "10px 0 0",
              fontFamily: displayFontFamily,
              fontSize: 13,
              lineHeight: 1.5,
              color: charcoal,
            }}
          >
            {description}
          </p>
        </div>

        <div style={{ flexShrink: 0 }}>
          <PdfCenteredSectionRule label="You will notice it when" />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginTop: 10,
            }}
          >
            {notices.map((line, index) => (
              <div
                key={`notice-${String(index)}`}
                className="mc-pdf-card"
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  background: palePink,
                  border: `1px solid ${terracotta}22`,
                  borderRadius: 14,
                  padding: "12px 12px",
                  fontFamily: bodyFontFamily,
                  boxSizing: "border-box",
                  minWidth: 0,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: `${terracotta}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Flag size={14} color={terracotta} strokeWidth={1.8} />
                </span>
                <p
                  style={{
                    margin: 0,
                    fontFamily: displayFontFamily,
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: charcoal,
                  }}
                >
                  {line}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div
          className="mc-pdf-card"
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            background: paleGreen,
            border: `1px solid ${green}33`,
            borderRadius: 16,
            padding: "14px 16px",
            fontFamily: bodyFontFamily,
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <ExitPathMark />
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: cardWhite,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
            }}
          >
            <ArrowRight size={16} color={green} strokeWidth={1.8} />
          </span>
          <div
            style={{
              minWidth: 0,
              flex: 1,
              position: "relative",
              zIndex: 1,
              paddingRight: 56,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: green,
              }}
            >
              How to get out
            </p>
            <p
              style={{
                margin: "8px 0 0",
                fontFamily: displayFontFamily,
                fontSize: 13,
                lineHeight: 1.5,
                color: charcoal,
              }}
            >
              {exitMove}
            </p>
          </div>
        </div>

        <TrapWayThroughPanel trapName={name} exitMove={exitMove} />
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterFailure;
